/**
 * JSON-LD (schema.org) builders for SEO, GEO and Answer-Engine Optimization (AEO).
 *
 * - LocalBusiness / RealEstateAgent — site-wide identity with geo
 * - RealEstateListing — per property
 * - Place — per Dubai zone
 * - BreadcrumbList — inner pages
 * - FAQPage — Zones index
 * - WebSite + SearchAction — sitelinks search box
 */

const SITE_URL = "https://dubai-storage-spaces.preview.emergentagent.com";
const BRAND = "WarehousesUAE.com";
const LEGAL = "Blueworld Realestate";
const PHONE = "+971588320669";
const EMAIL = "info@blueworldre.com";

// Dubai Silicon Oasis coordinates (Donna Towers)
const HQ_GEO = { lat: 25.1190, lng: 55.3814 };

// Approximate geo centroids of each Dubai zone for Place schema / "near me" queries
export const ZONE_GEO = {
  "JAFZA": { lat: 24.9853, lng: 55.0626, locality: "Jebel Ali" },
  "DIP": { lat: 24.9870, lng: 55.1747, locality: "Dubai Investments Park" },
  "Al Quoz": { lat: 25.1411, lng: 55.2335, locality: "Al Quoz" },
  "Ras Al Khor": { lat: 25.1860, lng: 55.3477, locality: "Ras Al Khor" },
  "Dubai South": { lat: 24.8907, lng: 55.1614, locality: "Dubai South" },
  "Dubai Industrial City": { lat: 24.9706, lng: 55.2225, locality: "Dubai Industrial City" },
  "Al Warsan": { lat: 25.1657, lng: 55.4163, locality: "Al Warsan" },
  "National Industrial Park": { lat: 24.9582, lng: 55.1230, locality: "Jebel Ali" },
  "Jebel Ali Industrial": { lat: 25.0157, lng: 55.1187, locality: "Jebel Ali Industrial Area" },
  "Sonapur": { lat: 25.1956, lng: 55.4459, locality: "Muhaisnah / Sonapur" },
  "Al Qusais": { lat: 25.2860, lng: 55.3686, locality: "Al Qusais" },
  "Umm Ramool": { lat: 25.2434, lng: 55.3640, locality: "Umm Ramool" },
  "Al Garhoud": { lat: 25.2384, lng: 55.3346, locality: "Al Garhoud" },
};

const ADDRESS_AE = {
  "@type": "PostalAddress",
  "streetAddress": "Office #304, Donna Towers, Dubai Silicon Oasis",
  "addressLocality": "Dubai",
  "addressRegion": "Dubai",
  "addressCountry": "AE",
};

/** Site-wide RealEstateAgent — emit once on Home + Contact. */
export const businessLd = () => ({
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  "@id": `${SITE_URL}/#organization`,
  "name": BRAND,
  "alternateName": [LEGAL, "Blue World Real Estate", "WarehousesUAE"],
  "url": SITE_URL,
  "logo": `${SITE_URL}/warehousesuae-icon-navy.png`,
  "image": `${SITE_URL}/og-default.png`,
  "telephone": PHONE,
  "email": EMAIL,
  "address": ADDRESS_AE,
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": HQ_GEO.lat,
    "longitude": HQ_GEO.lng,
  },
  "areaServed": [
    { "@type": "City", "name": "Dubai" },
    { "@type": "Country", "name": "United Arab Emirates" },
  ],
  "knowsLanguage": ["en", "ar"],
  "priceRange": "AED 50,000 – AED 25,000,000 / year",
  "openingHoursSpecification": [{
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Saturday", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
    "opens": "09:00",
    "closes": "18:00",
  }],
  "sameAs": [],
  "description": "WarehousesUAE.com — powered by Blueworld Realestate — is a Dubai-based commercial brokerage focused exclusively on industrial property: warehouses, industrial units, industrial plots, cold storage, showrooms, staff & labour accommodation and logistics facilities across JAFZA, DIP, Al Quoz, Ras Al Khor, Dubai South, DIC, Al Warsan and the National Industrial Park (NIP).",
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  "url": SITE_URL,
  "name": BRAND,
  "publisher": { "@id": `${SITE_URL}/#organization` },
  "inLanguage": ["en", "ar"],
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/properties?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

const PROP_TYPE_MAP = {
  warehouse: "Warehouse",
  cold_storage: "Cold storage",
  industrial_unit: "Industrial unit",
  industrial_plot: "Industrial plot",
  storage: "Storage facility",
  showroom: "Showroom",
  logistics: "Logistics hub",
  staff_accommodation: "Staff & labour accommodation",
};

/** RealEstateListing for a single property. */
export const propertyLd = (p) => {
  if (!p) return null;
  const geo = ZONE_GEO[p.zone];
  const img = (p.images || []).map((u) => (u && u.startsWith("http") ? u : `${SITE_URL}${u}`)).filter(Boolean);
  const human = PROP_TYPE_MAP[p.property_type] || p.property_type;
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "@id": `${SITE_URL}/properties/${p.id}#listing`,
    "name": p.title,
    "url": `${SITE_URL}/properties/${p.id}`,
    "description": p.description || `${human} for rent in ${p.zone}, Dubai — ${p.size_sqft?.toLocaleString?.() || p.size_sqft} sqft.`,
    "image": img.length ? img : [`${SITE_URL}/og-default.png`],
    "identifier": p.reference,
    "datePosted": p.created_at || undefined,
    "leaseLength": p.lease_term || undefined,
    "broker": { "@id": `${SITE_URL}/#organization` },
    "address": {
      ...ADDRESS_AE,
      "addressLocality": geo?.locality || p.zone || "Dubai",
    },
    ...(geo ? { "geo": { "@type": "GeoCoordinates", "latitude": geo.lat, "longitude": geo.lng } } : {}),
    "floorSize": p.size_sqft ? {
      "@type": "QuantitativeValue",
      "value": p.size_sqft,
      "unitCode": "FTK",
      "unitText": "Square feet",
    } : undefined,
    "offers": {
      "@type": "Offer",
      "price": p.price_aed,
      "priceCurrency": "AED",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": p.price_aed,
        "priceCurrency": "AED",
        "unitCode": "ANN",
        "referenceQuantity": { "@type": "QuantitativeValue", "value": 1, "unitText": "year" },
      },
      "availability": "https://schema.org/InStock",
      "businessFunction": "https://schema.org/LeaseOut",
      "seller": { "@id": `${SITE_URL}/#organization` },
    },
    "additionalProperty": [
      p.height_meters && { "@type": "PropertyValue", "name": "Clear height (m)", "value": p.height_meters },
      p.loading_bays && { "@type": "PropertyValue", "name": "Loading bays", "value": p.loading_bays },
      p.power_kw && { "@type": "PropertyValue", "name": "Power supply (kW)", "value": p.power_kw },
      p.parking_spaces && { "@type": "PropertyValue", "name": "Parking spaces", "value": p.parking_spaces },
      p.has_chiller && { "@type": "PropertyValue", "name": "Chiller", "value": "Yes" },
      p.has_office && { "@type": "PropertyValue", "name": "Office included", "value": "Yes" },
    ].filter(Boolean),
  };
};

/** Place schema for a Dubai zone landing page. */
export const zonePlaceLd = (zoneName, zoneDetail, listingsCount) => {
  const geo = ZONE_GEO[zoneName];
  return {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": `${zoneName}, Dubai`,
    "url": `${SITE_URL}/zones/${zoneDetail.slug}`,
    "description": zoneDetail.description,
    "image": zoneDetail.img,
    "address": { ...ADDRESS_AE, "addressLocality": geo?.locality || zoneName },
    ...(geo ? { "geo": { "@type": "GeoCoordinates", "latitude": geo.lat, "longitude": geo.lng } } : {}),
    "containedInPlace": { "@type": "City", "name": "Dubai" },
    ...(typeof listingsCount === "number" ? {
      "amenityFeature": [{ "@type": "LocationFeatureSpecification", "name": "Available listings", "value": listingsCount }],
    } : {}),
  };
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((it, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": it.name,
    "item": it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
  })),
});

export const faqLd = (qa) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": qa.map(([q, a]) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a },
  })),
});

export const itemListLd = (properties) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "numberOfItems": properties.length,
  "itemListElement": properties.slice(0, 30).map((p, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "url": `${SITE_URL}/properties/${p.id}`,
    "name": p.title,
  })),
});

export const ZONE_FAQ = [
  [
    "Which Dubai zone is best for customs-bonded warehousing and re-export?",
    "JAFZA (Jebel Ali Free Zone) and the National Industrial Park (NIP) are the two purpose-built customs-bonded zones in Dubai. JAFZA offers the deepest tenant base and direct Jebel Ali Port access; NIP is geared toward heavy manufacturing and large bonded logistics footprints.",
  ],
  [
    "Where can I find cold storage in Dubai for pharmaceuticals?",
    "DIP (Dubai Investments Park) hosts the largest concentration of GDP-compliant pharma cold storage in Dubai. Ras Al Khor and Dubai South also have temperature-controlled units suited for cross-dock and last-mile pharma distribution.",
  ],
  [
    "What is the cheapest industrial zone in Dubai?",
    "Al Warsan Industrial, Jebel Ali Industrial Area and Sonapur typically offer the lowest AED/sqft for mid-size industrial units and labour accommodation. JAFZA and DIC command a premium because of free-zone licensing and modern infrastructure.",
  ],
  [
    "Can a mainland company rent a warehouse in a free zone?",
    "Yes — but you cannot trade physically inside the free zone using a mainland licence. You can store goods there for re-export. To trade inside Dubai's mainland market with goods stored in a free zone, you must clear customs on import. WarehousesUAE.com helps clients match the right licence to the right zone.",
  ],
  [
    "What clear height should I look for in a Dubai warehouse?",
    "For palletised storage, 9–11 m clear height is standard. For high-bay racking or VNA systems, 12 m+ is preferred. For light assembly or showroom use, 6–8 m is sufficient. We verify clear height during property vetting before any shortlist.",
  ],
  [
    "How long does the typical Dubai warehouse lease run?",
    "1, 3 or 5 years are common, with annual escalation clauses. Free-zone leases (JAFZA, NIP, DIC) often start at 3 years. Mainland industrial units in Al Quoz, Ras Al Khor or Al Qusais are more flexible — 1-year renewable terms are widely available.",
  ],
];
